import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type GalleryItem } from '../api/client';
import { friendlyError } from '../lib/errors';

export type GalleryStatus = 'idle' | 'loading' | 'error';

export interface UseGalleryResult {
  items: GalleryItem[];
  status: GalleryStatus;
  errorMessage: string | null;
  optimisticAdd: (item: GalleryItem) => void;
  remove: (id: string) => void;
  restore: (item: GalleryItem) => void;
  refresh: () => Promise<void>;
}

export function useGallery(): UseGalleryResult {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [status, setStatus] = useState<GalleryStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus('loading');
    setErrorMessage(null);
    try {
      const resp = await api.listGallery(ctrl.signal);
      if (!ctrl.signal.aborted) {
        // Backend may return either a raw array or a wrapped
        // `{ items: [...], limit, offset }` envelope. Normalise to the
        // array the rest of the hook (and consumers) expect.
        const items = Array.isArray(resp) ? resp : (resp as any)?.items ?? [];
        setItems(items as GalleryItem[]);
        setStatus('idle');
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setStatus('error');
      setErrorMessage(friendlyError(err).message);
    }
  }, []);

  const optimisticAdd = useCallback((item: GalleryItem) => {
    setItems((prev) => [item, ...prev]);
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((existing) => existing.id !== id));
  }, []);

  const restore = useCallback((item: GalleryItem) => {
    setItems((prev) => {
      if (prev.some((existing) => existing.id === item.id)) return prev;
      return [item, ...prev];
    });
  }, []);

  useEffect(() => {
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  return { items, status, errorMessage, optimisticAdd, remove, restore, refresh };
}

/**
 * Upload an image with progress + a thumbnail preview while in flight.
 * Resolves to the persisted gallery item on success.
 */
export function useUpload(): {
  uploading: boolean;
  progress: number;
  errorMessage: string | null;
  upload: (opts: {
    file: File;
    title?: string;
    notes?: string;
  }) => Promise<GalleryItem | null>;
  reset: () => void;
} {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const upload = useCallback(
    async (opts: { file: File; title?: string; notes?: string }) => {
      setUploading(true);
      setProgress(0);
      setErrorMessage(null);
      try {
        const resp = await api.upload(opts.file, (p: number) =>
          setProgress(p),
        );
        return {
          id: resp.image_id,
          url: resp.image_url,
          thumbnail_url: resp.thumbnail_url,
          title: opts.title ?? opts.file.name,
          notes: opts.notes ?? null,
          created_at: new Date().toISOString(),
        };
      } catch (err) {
        setErrorMessage(friendlyError(err).message);
        return null;
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setErrorMessage(null);
    setUploading(false);
    setProgress(0);
  }, []);

  return { uploading, progress, errorMessage, upload, reset };
}
